import React, { useCallback, useEffect, useState } from 'react'

import { PasswordField } from '@octane/spark'

import { Field } from '../ChangePassword.styled'
import ValidationsField, { Validations } from './ValidationsField'

type Props = {
    passwordLabel?: string
    confirmPasswordLabel?: string
    onChange: (validPassword: string) => void
    newPasswordError: string
    onClearErrors: () => void
}

export function ChangePasswordFields({
    onChange,
    newPasswordError,
    onClearErrors,
    passwordLabel = 'New Password',
    confirmPasswordLabel = 'Confirm Password',
}: Readonly<Props>): JSX.Element {
    const [newPassword, setNewPassword] = useState('')
    const [confirmedPassword, setConfirmedPassword] = useState('')
    const [someNewPassError, setSomeNewPassError] = useState(false)

    const [validations, setValidations] = useState<Validations>({
        hasSymbols: null,
        passwordsMatch: null,
        hasMinCharacters: null,
        hasNumbers: null,
        hasLowerUpper: null,
    })

    const checkValidation = useCallback(() => {
        if (!newPassword) {
            setValidations({
                hasSymbols: null,
                passwordsMatch: null,
                hasMinCharacters: null,
                hasNumbers: null,
                hasLowerUpper: null,
            })

            return
        }

        const validationItems: Validations = {
            hasMinCharacters: newPassword.length >= 8,
            hasLowerUpper: /(?=.*?[a-z])(?=.*?[A-Z])/.test(newPassword),
            hasNumbers: /\d/.test(newPassword),
            hasSymbols: /[-!$%^&*()_+|~=`{}[\]:/;<>?,.@#]/.test(newPassword),
            passwordsMatch: confirmedPassword
                ? confirmedPassword === newPassword
                : null,
        }

        setValidations(validationItems)

        if (!Object.values(validationItems).some((i) => !i)) {
            onChange(newPassword)
            setSomeNewPassError(false)
        } else {
            setSomeNewPassError(
                !validationItems.hasMinCharacters ||
                    !validationItems.hasLowerUpper ||
                    !validationItems.hasNumbers ||
                    !validationItems.hasSymbols,
            )
        }
    }, [onChange, newPassword, confirmedPassword])

    function handleNewPasswordChange(value: string) {
        setNewPassword(value)
        onClearErrors()
    }

    useEffect(
        () => checkValidation(),
        [checkValidation, newPassword, confirmedPassword],
    )

    return (
        <>
            <Field>
                <PasswordField
                    id="new-password"
                    data-testid="new-password"
                    helpText={newPasswordError}
                    error={!!newPasswordError || someNewPassError}
                    label={passwordLabel}
                    maxLength={30}
                    value={newPassword}
                    onChange={(value) => handleNewPasswordChange(value)}
                    placeholder=""
                />
            </Field>
            <Field>
                <PasswordField
                    id="confirmed-password"
                    data-testid="confirmed-password"
                    label={confirmPasswordLabel}
                    maxLength={30}
                    value={confirmedPassword}
                    onChange={setConfirmedPassword}
                    error={
                        validations.passwordsMatch != null &&
                        !validations.passwordsMatch
                    }
                    placeholder=""
                />
            </Field>
            <ValidationsField validations={validations} />
        </>
    )
}

export default ChangePasswordFields
